import type {FC} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'next-i18next'
import {LoadingDots} from '@corratech/pylot-ui'
import {
    useSubscribeEmailToNewsletter,
    SubscribeInputVariables
} from '@corratech/pylot-footer-newsletter/src/hooks/use-subscribe-email-to-newsletter'
import {SubscriptionStatusesEnum} from '@pylot-data/fwrdschema.d'
import cn from 'classnames'
import s from '@commonStyles/footer/Footer.module.scss'
import React, { useState } from 'react'
import {getIsValidEmailAddress} from '@corratech/pylot-utils'

const Newsletter: FC = () => {
    const {t} = useTranslation(['common'])
    const {
        isLoading,
        subscribeEmailNewsletter
    } = useSubscribeEmailToNewsletter()
    const {
        register,
        handleSubmit,
        reset,
        formState: {errors}
    } = useForm<SubscribeInputVariables>({ mode: 'onSubmit' })

    const [errorMessage, setErrorMessage] = useState<string>('')
    const [successMessage, setSuccessMessage] = useState<string>('')

    const onSubmit = async (inputs: SubscribeInputVariables) => {
        try {
            const response = await subscribeEmailNewsletter(inputs)

            // Handle errors
            if (response?.errors?.length) {
                setErrorMessage(t(response?.errors[0]?.message))
                reset()
                return
            }

            if (response?.data?.subscribeEmailToNewsletter) {
                const status = response?.data?.subscribeEmailToNewsletter
                    ?.status!
                let message = ''

                if (status === SubscriptionStatusesEnum.Subscribed) {
                    message = t('footer|Thank you for your subscription!')
                }

                if (status === SubscriptionStatusesEnum.NotActive) {
                    message = t(
                        'footer|The subscription requires a confirmation. A confirmation email has been sent to specified email address to confirm the subscription.'
                    )
                }

                setSuccessMessage(
                    t('{{ statusText }}', {
                        statusText: message || status
                    })
                )
                setErrorMessage('')
                reset()
            }
        } catch (e) {
            setErrorMessage(t('footer|Something went wrong. Please try again'))
        }
    }

    return (
        <div className={s['newsletter-form']}>
            <h4 className={s['newsletter-form-heading']}>
                {t('footer|OOKA NEWSLETTER')}
            </h4>
            <p>
                {t(
                    'footer|Get the latest offers, exclusive deals and new product announcements!'
                )}
            </p>
            <form id={cn('newsletter-form')} onSubmit={handleSubmit(onSubmit)}>
                {!successMessage && (
                    <>
                        <div
                            className={cn(
                                s.main,
                                (errors?.email || errorMessage) &&
                                s['error-msg-wrapper']
                            )}
                        >
                            <div
                                className={cn(s['newsletter-input-div'], {
                                    [s['newsletter-input-div-compressed']]:
                                    errorMessage || errors?.email
                                })}
                            >
                                <label
                                    htmlFor="newsletter"
                                    className={s['newsletter-label']}
                                >
                                    {t('newsletter')}
                                </label>
                                <input
                                    className={cn(s.newsletter)}
                                    type="email"
                                    id="newsletter"
                                    placeholder={t('footer|Email Address')}
                                    {...register('email', {
                                        required: 'This field is required',
                                        validate: getIsValidEmailAddress
                                    })}
                                />
                            </div>
                            {errorMessage || errors?.email ? (
                                <div
                                    className={
                                        s['errors-newsletter-wrapper-mobile']
                                    }
                                >
                                    {errorMessage && (
                                        <div className={cn(s.error, 'error')}>
                                            {errorMessage}
                                        </div>
                                    )}
                                    {errors?.email && (
                                        <div className={cn(s.error, 'error')}>
                                            {t(
                                                errors?.email?.message as string
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <button
                                className={cn(s.submit, 'btn-submit')}
                                type="submit"
                                aria-label={t('submit')}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <LoadingDots/>
                                ) : (
                                    t('footer|SUBSCRIBE')
                                )}
                            </button>
                        </div>
                    </>
                )}
                {errorMessage || errors?.email ? (
                    <div className={s['errors-newsletter-wrapper']}>
                        {errorMessage && (
                            <div className={cn(s.error, 'error')}>
                                {errorMessage}
                            </div>
                        )}
                        {errors?.email && (
                            <div className={cn(s.error, 'error')}>
                                {t(errors?.email?.message as string)}
                            </div>
                        )}
                    </div>
                ) : null}

                {successMessage && (
                    <div
                        className={cn(
                            s.success,
                            s['newsletter-success-message']
                        )}
                    >
                    {successMessage}
                    </div>
                )}
            </form>
        </div>
    )
}

export default Newsletter
